import React, { useState, useMemo } from "react";
import { useCompanyItems } from "../../items/hooks/useCompanyItems.js";
import { useInventory } from "../hooks/useInventory.js";
import { MovementCard } from "../components/MovementCard.jsx";
import { StockAdjustSheet } from "../components/StockAdjustSheet.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { useListView } from "../../../shared/hooks/useListView.js";
import { DataTable } from "../../../shared/components/DataTable.jsx";
import { ViewToggle, StickyFooter } from "../../../shared/components/ListChrome.jsx";
import { stockLevelColumns, movementColumns } from "../components/inventoryColumns.jsx";

export function InventoryPage() {
  const { items, loading: itemsLoading, refetch: refetchItems } = useCompanyItems();
  const { movements, loading: movementsLoading, refetch: refetchMovements, adjustStock } = useInventory();

  const [activeTab, setActiveTab] = useState("LEVELS"); // 'LEVELS' | 'HISTORY'
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItemForAdjust, setSelectedItemForAdjust] = useState(null);
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);

  // Compute metrics
  const goodsItems = useMemo(() => items.filter((i) => i.itemType !== "service"), [items]);
  const lowStockCount = useMemo(() => {
    return goodsItems.filter((i) => Number(i.stock || 0) <= Number(i.reorderLevel || 10)).length;
  }, [goodsItems]);

  // Filter items
  const filteredItems = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return goodsItems.filter((item) => {
      const name = (item.itemName || item.name || "").toLowerCase();
      const code = (item.gstCode || item.sku || "").toLowerCase();
      return !term || name.includes(term) || code.includes(term);
    });
  }, [goodsItems, searchTerm]);

  // Filter movements
  const filteredMovements = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return movements.filter((m) => {
      const name = (m.itemName || "").toLowerCase();
      const reason = (m.reason || "").toLowerCase();
      return !term || name.includes(term) || reason.includes(term);
    });
  }, [movements, searchTerm]);

  const listView = useListView("inventory");

  const handleOpenAdjust = (item) => {
    setSelectedItemForAdjust(item);
    setIsAdjustOpen(true);
  };

  const handleAdjustSuccess = async (payload) => {
    await adjustStock(payload);
    await refetchItems();
  };

  return (
    <div className="inventory-page">
      <div style={{ marginBottom: "1rem", width: "100%", minWidth: 0 }}>
        <h2 style={{ fontSize: "1.25rem", fontWeight: "700" }}>Inventory & Stock</h2>
        <p style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>
          Real-time stock audit and goods movement history
        </p>
      </div>

      {/* KPI Metrics Row */}
      <div className="metrics-row">
        <div className="metric-card">
          <div className="metric-label">Goods Catalog</div>
          <div className="metric-value">{goodsItems.length}</div>
        </div>
        <div className="metric-card" style={{ borderLeft: lowStockCount > 0 ? "3px solid var(--warning)" : "1px solid var(--border-subtle)" }}>
          <div className="metric-label">Low Stock Alerts</div>
          <div className="metric-value" style={{ color: lowStockCount > 0 ? "var(--warning)" : "var(--success)" }}>
            {lowStockCount}
          </div>
        </div>
      </div>

      {/* Sub-tabs + search (one sticky toolbar row on desktop) */}
      <div className="list-toolbar">
        <div className="filter-pills-row">
          <button
            type="button"
            className={`filter-pill ${activeTab === "LEVELS" ? "active" : ""}`}
            onClick={() => setActiveTab("LEVELS")}
          >
            📦 Stock Levels ({goodsItems.length})
          </button>
          <button
            type="button"
            className={`filter-pill ${activeTab === "HISTORY" ? "active" : ""}`}
            onClick={() => setActiveTab("HISTORY")}
          >
            📜 Movement History ({movements.length})
          </button>
        </div>

        {/* Search Bar */}
        <div className="search-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder={activeTab === "LEVELS" ? "Search stock items..." : "Search movement logs..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <ViewToggle view={listView.view} onChange={listView.setView} />
      </div>

      {/* View 1: Stock Levels */}
      {activeTab === "LEVELS" && (
        <div>
          {itemsLoading && (
            <div className="state-box">
              <div className="state-icon">⏳</div>
              <div className="state-title">Loading stock inventory...</div>
            </div>
          )}

          {!itemsLoading && filteredItems.length === 0 && (
            <div className="state-box">
              <div className="state-icon">📦</div>
              <div className="state-title">No stock items found</div>
              <div className="state-desc">
                {searchTerm ? "Try searching with another item name." : "Add physical goods in the Items tab first."}
              </div>
            </div>
          )}

          {listView.showTable ? (
            <DataTable
              columns={stockLevelColumns}
              rows={filteredItems}
              onRowClick={handleOpenAdjust}
              storageKey="stock_levels"
              noun="items"
              resetKey={searchTerm}
            />
          ) : (
            <div className="stock-items-list card-grid">
              {filteredItems.map((item) => {
                const currentStock = Number(item.stock || 0);
                const reorder = Number(item.reorderLevel || 10);
                const isLow = currentStock <= reorder;

                return (
                  <Card key={item.id} style={{ marginBottom: "0.75rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "600", fontSize: "0.95rem" }}>
                          {item.itemName || item.name}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
                          HSN: {item.gstCode || "N/A"} • Reorder threshold: {reorder} {item.unit || "pcs"}
                        </div>
                        <div style={{ marginTop: "0.5rem" }}>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleOpenAdjust(item)}
                            style={{ fontSize: "0.75rem", padding: "0.25rem 0.65rem", minHeight: "30px" }}
                          >
                            ⚖️ Adjust Stock
                          </Button>
                        </div>
                      </div>

                      <div style={{ textAlign: "right" }}>
                        <div style={{
                          fontSize: "1.25rem",
                          fontWeight: "700",
                          color: isLow ? "var(--warning)" : "var(--success)"
                        }}>
                          {currentStock} <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{item.unit || "pcs"}</span>
                        </div>
                        <Badge variant={isLow ? "warning" : "success"}>
                          {isLow ? "Low Stock" : "In Stock"}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* View 2: Movement History */}
      {activeTab === "HISTORY" && (
        <div>
          {movementsLoading && (
            <div className="state-box">
              <div className="state-icon">⏳</div>
              <div className="state-title">Loading stock history...</div>
            </div>
          )}

          {!movementsLoading && filteredMovements.length === 0 && (
            <div className="state-box">
              <div className="state-icon">📜</div>
              <div className="state-title">No movement logs recorded</div>
              <div className="state-desc">
                Stock movements will appear here whenever you adjust stock or create sales invoices.
              </div>
            </div>
          )}

          {listView.showTable ? (
            <DataTable
              columns={movementColumns}
              rows={filteredMovements}
              storageKey="stock_movements"
              noun="movements"
              resetKey={searchTerm}
            />
          ) : (
            <div className="movements-list card-grid">
              {filteredMovements.map((movement) => (
                <MovementCard key={movement.id} movement={movement} />
              ))}
            </div>
          )}
        </div>
      )}

      <StickyFooter
        shown={activeTab === "LEVELS" ? filteredItems.length : filteredMovements.length}
        total={activeTab === "LEVELS" ? goodsItems.length : movements.length}
        noun={activeTab === "LEVELS" ? "stock items" : "movements"}
        chips={activeTab === "LEVELS" ? [{ label: "Low stock", value: lowStockCount, tone: lowStockCount > 0 ? "warning" : "success" }] : []}
      />

      {/* Stock Adjustment Bottom Sheet */}
      <StockAdjustSheet
        isOpen={isAdjustOpen}
        onClose={() => {
          setIsAdjustOpen(false);
          setSelectedItemForAdjust(null);
        }}
        item={selectedItemForAdjust}
        onAdjustSuccess={handleAdjustSuccess}
      />
    </div>
  );
}

export default InventoryPage;
